import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import classes from './ProjectDetailPage.module.css';
import axios from '../../axios';
import { Card, Button } from '../UI';
import ProjectForm from './ProjectForm';
import ProjectView from './ProjectView';
import ProjectDevicesTable from './ProjectDevicesTable';
import { downloadDevices } from '../../utils/downloadDevices';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    notes: '',
    status: 'Preparation',
    createdAt: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeviceList, setShowDeviceList] = useState(false);
  const [unassignedDevices, setUnassignedDevices] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [devicesToAdd, setDevicesToAdd] = useState([])
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadProject();
    loadDevices();
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/projects/${id}`);
      if (response.data && response.data.success && response.data.project) {
        const projectData = response.data.project;
        setProject(projectData);
        const createdAtDate = projectData.createdAt 
          ? new Date(projectData.createdAt).toISOString().split('T')[0]
          : '';
        setFormData({
          name: projectData.name || '',
          location: projectData.location || '',
          notes: projectData.notes || '',
          status: projectData.status || 'Preparation',
          createdAt: createdAtDate
        });
      }
    } catch (error) {
      console.error('Failed to load project:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      setDevicesLoading(true);
      const response = await axios.get(`/projects/${id}/devices`);
      if (response.data && response.data.success && response.data.devices) {
        setDevices(response.data.devices);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };
  
  const removeDevices = async (projectId, deviceIds) => {
    try {
      setDevicesLoading(true);
      const response = await axios.delete(`/projects/${projectId}/devices`, {
        data: { deviceIds }
      });

      if (response.data && response.data.success) {
        await loadDevices();
        setSelectedRows([]);
        setClearSelectedRows(prev => !prev);
      } else {
        alert(response.data?.message || 'Failed to remove devices');
      }
    } catch (error) {
      console.error('Failed to remove devices:', error);
      const errorMessage = error.response?.data?.message || 'Failed to remove devices';
      alert(errorMessage);
    } finally {
      setDevicesLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${d}/${m}/${date.getFullYear()}`;
  };

  const handleRowSelected = (state) => {
    setSelectedRows(state.selectedRows || []);
  };

  const handleDownloadSelected = () => {
    if (selectedRows.length === 0) return;
    const selectedIds = selectedRows.map(row => row.id)
    downloadDevices(selectedIds, id, project.name);
  };

  const handleDownloadAll = () => {
    if (devices.length === 0) return;
    const selectedIds = devices.map(row => row.id);
    downloadDevices(selectedIds, id, project.name);
  };

  const handleDownloadRow = (row) => {
    const Id = row.id;
    if (Id === "") return;
    const idToDownload = [Id];
    downloadDevices(idToDownload, id, project.name);
  };

  const handleRemoveSelected = () => {
    if (selectedRows.length === 0) return;
    removeDevices(id, selectedRows.map(row => row.id));
  };

  const handleAddDevices = async () => {
    if (showDeviceList) {
      setShowDeviceList(false);
      return;
    }
    try {
      setUnassignedLoading(true);
      setShowDeviceList(true);
      const res = await axios.get('devices/list/unassigned');
      if (res.data && res.data.success && res.data.devices) {
        setUnassignedDevices(res.data.devices);
      }
    } catch (error) {
      console.error('Failed to load unassigned devices:', error);
    } finally {
      setUnassignedLoading(false);
    }
  };

  const filteredUnassigned = useMemo(() => {
    if (!deviceSearch.trim()) return unassignedDevices;
    const q = deviceSearch.toLowerCase();
    return unassignedDevices.filter(d =>
      (d.serialNumber && d.serialNumber.toLowerCase().includes(q)) ||
      (d.deviceEUI && d.deviceEUI.toLowerCase().includes(q)) ||
      (d.deviceModel && d.deviceModel.toLowerCase().includes(q))
    );
  }, [unassignedDevices, deviceSearch]);

  useEffect(() => {
    if (!showDeviceList) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDeviceList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDeviceList]);

  const selectDeviceToAdd = (checked, device) => {
    setDevicesToAdd((prev) => {
      if (checked) {
        if (prev.some((d) => d.id === device.id)) return prev;
        return [...prev, device];
      }
      return prev.filter((d) => d.id !== device.id);
    });
  }

  const handleAssignDevices = async () => {
    try {
      const deviceId = devicesToAdd[0].id
      const devices = devicesToAdd.map((device) => device.id)
      const res = await axios.put(`/devices/${deviceId}/reassign-project`, { projectId: id, deviceIds: devices })
      loadDevices()
    } catch (error) {
      console.error(error)
    } finally {
      setShowDeviceList(false)
      setDeviceSearch('')
      setDevicesToAdd([])
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Project name must be at least 2 characters';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      const updateData = {
        name: formData.name.trim(),
        location: formData.location.trim(),
        notes: formData.notes.trim(),
        status: formData.status
      };
      
      if (formData.createdAt) {
        updateData.createdAt = formData.createdAt;
      }
      
      const response = await axios.put(`/projects/${id}`, updateData);

      if (response.data && response.data.success) {
        await loadProject();
        setIsEditing(false);
        if (response.data.project) {
          setProject(prev => ({
            ...prev,
            ...response.data.project
          }));
        }
      } else {
        alert(response.data?.message || 'Failed to update project');
      }
    } catch (error) {
      console.error('Failed to update project:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update project';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (project) {
      const createdAtDate = project.createdAt 
        ? new Date(project.createdAt).toISOString().split('T')[0]
        : '';
      setFormData({
        name: project.name || '',
        location: project.location || '',
        notes: project.notes || '',
        status: project.status || 'Preparation',
        createdAt: createdAtDate
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  const handleDelete = async () => {
    const confirmMessage = `Are you sure you want to delete the project "${project.name}"?\n\nThis will unassign all devices from this project, but the devices will not be deleted.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleting(true);
      const response = await axios.delete(`/projects/${id}`);

      if (response.data && response.data.success) {
        navigate('/projects');
      } else {
        alert(response.data?.message || 'Failed to delete project');
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete project';
      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };


  if (loading) {
    return (
      <div className={classes.projectDetailPage}>
        <div className={classes.message}>Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className={classes.projectDetailPage}>
        <div className={classes.message}>Project not found</div>
      </div>
    );
  }

  return (
    <div className={classes.projectDetailPage}>
      <div className={classes.header}>
        <div className={classes.headerInfo}>
          <h1 className={classes.projectTitle}>{project.name || 'Project'}</h1>
          <p className={classes.projectLocation}>{project.location || ''}</p>
        </div>
        <div className={classes.addDevicesWrapper} ref={dropdownRef}>
          <Button
            variant="primary"
            onClick={handleAddDevices}
            className={classes.addDevicesButton}
          >
            Add devices
          </Button>
          {showDeviceList && (
            <div className={classes.deviceDropdown}>
              <input
                type="text"
                className={classes.deviceDropdownSearch}
                placeholder="Search devices..."
                value={deviceSearch}
                onChange={(e) => setDeviceSearch(e.target.value)}
                autoFocus
              />
              <div className={classes.deviceDropdownList}>
                {unassignedLoading ? (
                  <div className={classes.deviceDropdownEmpty}>Loading...</div>
                ) : filteredUnassigned.length === 0 ? (
                  <div className={classes.deviceDropdownEmpty}>
                    {deviceSearch ? 'No matches' : 'No unassigned devices'}
                  </div>
                ) : (
                  filteredUnassigned.map(device => (
                    <div key={device.id} className={classes.deviceDropdownItem}>
                      <input 
                        type='checkbox' 
                        id={`checkbox-${device.id}`} 
                        onChange={(e) => selectDeviceToAdd(e.target.checked, device)}
                        className={classes.deviceDropdownItemCheckbox}></input>
                      <label className={classes.deviceDropdownLabel} htmlFor={`checkbox-${device.id}`}>
                        <div className={classes.deviceDropdownItemMain}>
                          {device.serialNumber}
                        </div>
                        <div className={classes.deviceDropdownItemSub}>
                          {device.deviceModel || '—'}
                        </div>
                      </label>
                    </div>
                  ))
                )}
                <div className={classes.deviceDropdownSave}>
                  <Button
                    variant="primary"
                    onClick={handleAssignDevices}
                    disabled={devicesToAdd.length === 0}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={classes.content}>
        <div className={classes.leftColumn}>
          <Card padding="small">
            {isEditing ? (
              <ProjectForm
                formData={formData}
                errors={errors}
                saving={saving}
                onChange={handleChange}
                onSubmit={(e) => { e.preventDefault(); handleSave(); }}
                onCancel={handleCancel}
                onDownloadAll={handleDownloadAll}
                formatDate={formatDate}
                project={project}
              />
            ) : (
              <ProjectView
                project={project}
                formatDate={formatDate}
                onEdit={() => setIsEditing(true)}
                onDelete={handleDelete}
                onDownloadAll={handleDownloadAll}
                deleting={deleting}
              />
            )}
          </Card>
        </div>

        <div className={classes.rightColumn}>
          <ProjectDevicesTable
            devices={devices}
            loading={devicesLoading}
            selectedRows={selectedRows}
            clearSelectedRows={clearSelectedRows}
            onRowSelected={handleRowSelected}
            onDownloadSelected={handleDownloadSelected}
            onDownloadRow={handleDownloadRow}
            onRemoveSelected={handleRemoveSelected}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;